import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { LogOut } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { deleteJSON } from '@/lib/api'
import { displayPortalDate, type MemberStatusSource } from './memberPortalPresentation'

export function MemberStatusSources({ sources }: { sources: MemberStatusSource[] }) {
  const queryClient = useQueryClient()
  const [sourceToLeave, setSourceToLeave] = React.useState<MemberStatusSource | null>(null)
  const leave = useMutation({
    mutationFn: (source: MemberStatusSource) =>
      deleteJSON(
        `/api/v1/member_portal/organizations/${source.organization_id}/roster/${source.roster_place_id}`,
      ),
    onSuccess: () => {
      setSourceToLeave(null)
      queryClient.invalidateQueries({ queryKey: ['member-portal-session'] })
    },
  })
  const rosterSources = sources.filter((source) => source.kind === 'organization_roster')
  if (rosterSources.length === 0) return null

  return (
    <section className="mt-6 space-y-3" aria-label="Member status sources">
      <h2 className="text-body font-semibold">Where your member status comes from</h2>
      {rosterSources.map((source) => (
        <Card
          key={`${source.organization_id}-${source.roster_place_id}`}
          className="overflow-hidden"
        >
          <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-1 p-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <p className="text-body font-medium">Member via {source.label}</p>
                <Badge variant={source.status === 'Active' ? 'success' : 'warning'}>
                  {source.status}
                </Badge>
              </div>
              <p className="mt-1 text-caption text-muted-foreground">{source.level}</p>
            </div>
            <p className="shrink-0 text-body-sm text-muted-foreground">
              {source.status === 'Lapsed'
                ? 'Lapsed'
                : source.renews_on
                  ? `Renews ${displayPortalDate(source.renews_on)}`
                  : null}
            </p>
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-2">
            <p className="text-caption text-muted-foreground">
              {source.status === 'Lapsed'
                ? `Member status resumes when ${source.label} renews.`
                : source.added_by && source.added_on
                  ? `Added by ${source.added_by} · ${displayPortalDate(source.added_on)}`
                  : null}
            </p>
            {source.status !== 'Lapsed' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-transparent bg-transparent text-muted-foreground shadow-none hover:text-foreground"
                onClick={() => setSourceToLeave(source)}
                disabled={leave.isPending}
              >
                <LogOut className="mr-2 size-4" aria-hidden="true" />
                {leave.isPending ? 'Leaving...' : 'Leave roster'}
              </Button>
            )}
          </div>
          {leave.isError ? (
            <Alert variant="destructive" className="mx-4 mb-4">
              We couldn&apos;t leave this roster. Try again.
            </Alert>
          ) : null}
        </Card>
      ))}
      <Dialog
        open={sourceToLeave !== null}
        onOpenChange={(open) => !open && setSourceToLeave(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave the {sourceToLeave?.label} roster?</DialogTitle>
            <DialogDescription>
              Your member status through {sourceToLeave?.label} ends now. Your contact record and
              history stay.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSourceToLeave(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => sourceToLeave && leave.mutate(sourceToLeave)}
              disabled={leave.isPending}
            >
              Leave roster
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
